<template>
  <section class="page-hero">
    <div class="page-hero__top" v-if="tvaMq !== 'desktop'">
      <Breadcrumb v-if="tvaMq == 'tablet'" />

      <div class="page-hero__right">
        <p class="page-hero__right-label" v-if="tvaMq == 'tablet'">
          {{ chapterLabel }}
        </p>
        <router-link :to="{ params: { page: nextPage } }">
          <ButtonPrimary
            :label="$tm('general.ctas.next')"
            :iconAfter="arrowRightSmall"
            v-if="tvaMq == 'tablet'"
          />
        </router-link>
      </div>

      <div :class="['page-hero__anim', { loaded }]">
        <canvas ref="riveAnimation" width="2170" height="2000"></canvas>
      </div>
    </div>

    <Breadcrumb v-if="tvaMq === 'desktop'" />

    <div class="page-hero__bottom" v-if="tvaMq !== 'desktop'">
      <router-link :to="{ params: { page: nextPage } }">
        <ButtonPrimary
          :label="$tm('general.ctas.next')"
          :iconAfter="arrowRightSmall"
          class="page-hero__bottom-next"
          v-if="tvaMq == 'mobile'"
        />
      </router-link>
      <IntestationHero :title="title" :subtitle="subtitle" :pre="pre" />
    </div>

    <div class="page-hero__left" v-if="tvaMq === 'desktop'">
      <IntestationHero :title="title" :subtitle="subtitle" :pre="pre" />
    </div>

    <div class="page-hero__right" v-if="tvaMq === 'desktop'">
      <p class="page-hero__right-label">{{ chapterLabel }}</p>
      <router-link :to="{ params: { page: nextPage } }">
        <ButtonPrimary :label="buttonLabel" :iconAfter="arrowRightSmall" />
      </router-link>
    </div>

    <div :class="['page-hero__anim', { loaded }]" v-if="tvaMq === 'desktop'">
      <canvas ref="riveAnimation" width="2170" height="2000"></canvas>
    </div>
    <!-- 
    <PageHeroContent
      class="page-hero__content"
      :currentPage="currentPage"
      :title="title"
      :introText="introText"
      :pdfLink="pdfLink"
    /> -->
  </section>
</template>

<script setup>
// import PageHeroContent from "../general/PageHeroContent.vue";
import IntestationHero from "../molecules/IntestationHero.vue";
import ButtonPrimary from "../atoms/ButtonPrimary.vue";
import Breadcrumb from "@/components/molecules/Breadcrumb.vue";
import { arrowRightSmall } from "@/assets/icons/icons.js";
import { Rive } from "@rive-app/canvas";
import { ref, onMounted, inject, computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
  currentPage: String,
  nextPage: String,
  pre: String,
  title: String,
  subtitle: String,
  chapterLabel: String,
  buttonLabel: String,
});

const i18n = useI18n();
const { tm } = useI18n();
const baseUrl = inject("$baseUrl");
const tvaMq = inject("$tvaMq");
const riveAnimation = ref(null);
const pageKey = computed(() =>
  i18n.locale.value == "en"
    ? tm("translation")[props.currentPage]
    : props.currentPage
);
const loaded = ref(false);

let rive;

onMounted(() => {
  if (pageKey.value) {
    let artboard = pageKey.value[0].toUpperCase() + pageKey.value.slice(1);

    rive = new Rive({
      src: `${baseUrl}anims/${pageKey.value}.riv`,
      canvas: riveAnimation.value,
      artboard: artboard,
      autoplay: true,
      onLoad: () => {
        rive.resizeDrawingSurfaceToCanvas();
        loaded.value = true;
      },
    });

    rive.play();
  }
});
</script>

<style lang="scss" scoped>
.page-hero {
  height: calc(var(--vh) * 100);
  background: linear-gradient(
      0deg,
      rgba(34, 34, 34, 0.25) 0%,
      rgba(34, 34, 34, 0.25) 100%
    ),
    var(--page-color);
  overflow: hidden;
  position: relative;

  .tablet &,
  .mobile & {
    display: flex;
    flex-direction: column;
  }

  &__top {
    height: 60%;
    position: relative;
    overflow: hidden;
  }

  &__bottom {
    height: 40%;
    border-top: 1px solid var(--text-disabled);

    &-next {
      position: absolute;
      right: toRem(24);
      top: toRem(16);
    }

    .mobile & {
      position: relative;
    }
  }

  &__anim {
    position: absolute;

    right: -20%;
    bottom: 0;
    height: 150%;
    aspect-ratio: 1 / 1;
    z-index: 0;

    & > canvas {
      width: 100%;
      height: 100%;
      opacity: 0;
      transition: opacity 1s ease;
    }
    &.loaded {
      & > canvas {
        opacity: 1;
      }
    }

    .tablet &,
    .mobile & {
      height: 200%;

      bottom: -20%;
    }
  }

  &__left {
    height: 100%;
    position: relative;
    z-index: 1;
  }

  &__right {
    position: absolute;
    height: 100%;
    width: calc(var(--page-padding-h) + var(--page-padding-h-offset));
    top: 0;
    right: 0;
    border-left: 0.5px solid var(--color-white);
    background: rgba(34, 34, 34, 0.25);
    backdrop-filter: blur(25px);
    padding: toRem(150) toRem(24);
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    z-index: 1;

    &-label {
      color: var(--color-text-weaker);
      margin-bottom: toRem(12);
      @include dida-s;
    }

    .tablet &,
    .mobile & {
      backdrop-filter: none;
      padding: 9.375rem 2rem 1.5rem 1rem;
    }
    .tablet & {
      width: 12.225rem;
    }

    .mobile & {
      width: 5.6rem;
    }
  }

  .desktop &,
  .large & {
    padding: 0 calc(var(--page-padding-h) + var(--page-padding-h-offset));
  }

  .mobile &,
  .padding & {
    @include home-side-stripes;
  }

  &__content {
    .desktop &,
    .large & {
      width: var(--left-space);
    }
  }

  &__video {
    top: 0;

    ::v-deep(video) {
      // left: calc(var(--page-padding-h) + var(--left-space));
      // transform: translateY(-50%);
    }

    .desktop &,
    .large & {
      left: calc(var(--page-padding-h) + var(--left-space));
      width: calc(100% - var(--page-padding-h) * 2 - var(--left-space));
      height: calc(var(--vh) * 100);
      // clip-path: polygon(41.5% 0, 85% 0%, 85% 100%, 41.5% 100%);
    }

    .mobile &,
    .tablet & {
      position: relative;
      left: 0;
      height: 50%;

      ::v-deep(video) {
        width: 100%;
        height: auto;
      }
    }
  }
}
</style>
